import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import type { IncidentLifecycleData } from '@/services/analytics.service';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const STAGE_COLORS = ['#3B82F6', '#8B5CF6', '#F59E0B', '#10B981', '#6B7280'];

interface IncidentFunnelProps {
  data: IncidentLifecycleData;
}

const IncidentFunnel: React.FC<IncidentFunnelProps> = ({ data }) => {
  if (!data.stages || data.stages.length === 0) {return null;}

  const maxCount = Math.max(...data.stages.map(s => s.count), 1);

  // Stage duration chart data (exclude Reported which has null duration)
  const durationData = data.stages
    .filter(s => s.avgDurationHours !== null && s.avgDurationHours !== undefined && s.avgDurationHours > 0)
    .map((s) => ({
      name: s.name,
      hours: s.avgDurationHours as number,
      isBottleneck: data.bottleneck?.stage === s.name,
      color: STAGE_COLORS[data.stages.indexOf(s) % STAGE_COLORS.length],
    }));

  return (
    <div className="space-y-6">
      {/* Funnel Visualization */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Incident Lifecycle Funnel</CardTitle>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                Reported &rarr; Assigned &rarr; In Progress &rarr; Resolved &rarr; Closed
              </p>
            </div>
            {data.bottleneck && (
              <div className="px-3 py-1.5 bg-warning-100 dark:bg-warning-900/20 text-warning-700 dark:text-warning-400 rounded-lg text-sm font-medium">
                Bottleneck: {data.bottleneck.stage} ({data.bottleneck.avgDurationHours}h avg)
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {data.stages.map((stage, index) => {
              const widthPercent = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
              const dropOff = data.dropOffRates[index - 1];

              return (
                <motion.div
                  key={stage.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Drop-off annotation */}
                  {dropOff && dropOff.rate > 0 && (
                    <div className="flex items-center justify-center py-1">
                      <span className="text-xs text-secondary-400 dark:text-secondary-500">
                        &darr; {dropOff.rate}% drop-off
                      </span>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    {/* Stage label */}
                    <div className="w-28 text-right">
                      <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                        {stage.name}
                      </span>
                    </div>

                    {/* Funnel bar */}
                    <div className="flex-1 flex justify-center">
                      <div
                        className="h-10 rounded-lg flex items-center justify-center transition-all duration-500"
                        style={{
                          width: `${Math.max(widthPercent, 8)}%`,
                          backgroundColor: STAGE_COLORS[index % STAGE_COLORS.length],
                          opacity: 0.85,
                        }}
                      >
                        <span className="text-sm font-bold text-white drop-shadow-sm">
                          {stage.count}
                        </span>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="w-20 text-left">
                      {stage.avgDurationHours !== null && stage.avgDurationHours !== undefined ? (
                        <span className={`text-xs ${
                          data.bottleneck?.stage === stage.name
                            ? 'text-warning-600 dark:text-warning-400 font-semibold'
                            : 'text-secondary-500 dark:text-secondary-400'
                        }`}>
                          {stage.avgDurationHours}h avg
                        </span>
                      ) : (
                        <span className="text-xs text-secondary-400">&mdash;</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Stage Duration Chart */}
      {durationData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Average Stage Duration (Hours)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={durationData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickLine={{ stroke: '#cbd5e1' }}
                  axisLine={{ stroke: '#cbd5e1' }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickLine={{ stroke: '#cbd5e1' }}
                  axisLine={{ stroke: '#cbd5e1' }}
                  width={100}
                />
                <Tooltip
                  formatter={(value: number) => [`${value}h`, 'Avg Duration']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '8px 12px',
                  }}
                />
                <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
                  {durationData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.isBottleneck ? '#F59E0B' : entry.color}
                      strokeWidth={entry.isBottleneck ? 2 : 0}
                      stroke={entry.isBottleneck ? '#D97706' : 'none'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default IncidentFunnel;
